import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  useTheme,
  alpha,
  Skeleton,
  Grid,
  Button,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import FilterListIcon from '@mui/icons-material/FilterList';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BottomNav from './BottomNav';
import { db, auth } from '../firebase';
import { type DocumentData, type QueryDocumentSnapshot, Timestamp } from 'firebase/firestore';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import {
  COMMISSIONS_PAGE_SIZE,
  fetchCommissionPage,
  fetchCommissionSummary,
  type CommissionRecord,
  type CommissionSummary,
} from '../services/commissionData';

type Commission = CommissionRecord;

const CommissionsView: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [summaryTotals, setSummaryTotals] = useState<CommissionSummary>({
    commissionTotal: 0,
    saleSplitsTotal: 0,
  });
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [startDate, setStartDate] = useState<Date | null>(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState<Date | null>(endOfMonth(new Date()));
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const convertTimestamp = (timestamp: any): Date => {
    if (!timestamp) return new Date();
    if (timestamp instanceof Timestamp) return timestamp.toDate();
    if (timestamp.seconds) return new Date(timestamp.seconds * 1000);
    return new Date(timestamp);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        try {
          const userDoc = await db.collection('users').doc(currentUser.uid).get();
          const data = userDoc.data();
          setUserData({
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: currentUser.displayName || data?.firstName || 'Staff Member',
            staffId: data?.staffId,
            ...data
          });
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      }
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    if (!userData?.email) return;

    let cancelled = false;

    const loadInitialPage = async () => {
      setLoading(true);
      setErrorMessage(null);
      try {
        const result = await fetchCommissionPage({
          userEmail: userData.email,
          startDate,
          endDate,
          pageSize: COMMISSIONS_PAGE_SIZE,
        });

        if (cancelled) return;

        setCommissions(result.commissions);
        setLastVisible(result.lastVisible);
        setHasMore(result.hasMore);
      } catch (error) {
        console.error('Error fetching commissions:', error);
        if (!cancelled) {
          setCommissions([]);
          setLastVisible(null);
          setHasMore(false);
          setErrorMessage('Unable to load commissions right now. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    const loadSummaryTotals = async () => {
      setSummaryLoading(true);
      setSummaryTotals({ commissionTotal: 0, saleSplitsTotal: 0 });
      try {
        const summary = await fetchCommissionSummary({
          userEmail: userData.email,
          startDate,
          endDate,
        });

        if (!cancelled) {
          setSummaryTotals(summary);
        }
      } catch (error) {
        console.error('Error fetching commission summary:', error);
        if (!cancelled) {
          setSummaryTotals({ commissionTotal: 0, saleSplitsTotal: 0 });
        }
      } finally {
        if (!cancelled) {
          setSummaryLoading(false);
        }
      }
    };

    loadInitialPage();
    loadSummaryTotals();

    return () => {
      cancelled = true;
    };
  }, [userData?.email, startDate, endDate]);

  const handleLoadMore = async () => {
    if (!userData?.email || !lastVisible || loadingMore) {
      return;
    }

    setLoadingMore(true);
    setErrorMessage(null);

    try {
      const result = await fetchCommissionPage({
        userEmail: userData.email,
        startDate,
        endDate,
        cursor: lastVisible,
        pageSize: COMMISSIONS_PAGE_SIZE,
      });

      setCommissions((previous) => [...previous, ...result.commissions]);
      setLastVisible(result.lastVisible);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Error loading more commissions:', error);
      setErrorMessage('Unable to load more commissions right now. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  const commissionTotal = summaryTotals.commissionTotal;
  const saleSplitsTotal = summaryTotals.saleSplitsTotal;

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text('Commission Report', 14, 22);
      doc.setFontSize(11);
      const dateRange = startDate && endDate
        ? `${format(startDate, 'dd/MM/yyyy')} - ${format(endDate, 'dd/MM/yyyy')}`
        : 'All time';
      doc.text(`Period: ${dateRange}`, 14, 30);
      doc.text(`Staff: ${userData?.displayName || 'Unknown'}`, 14, 38);
      doc.text(`Commission Total: ${formatCurrency(commissionTotal)}`, 14, 46);
      doc.text(`Sale Splits Total: ${formatCurrency(saleSplitsTotal)}`, 14, 54);

      const tableColumn = ["Date", "Sale #", "Service", "Price", "Amount", "Status"];
      const tableRows: any[] = [];

      commissions.forEach(commission => {
        const formattedDate = format(convertTimestamp(commission.date), 'dd/MM/yyyy');
        const status = commission.status === 'voided' ? 'Voided' :
                      commission.status === 'paid' ? 'Paid' : 'Pending';

        tableRows.push([
          formattedDate,
          commission.saleNumber,
          commission.serviceName,
          formatCurrency(commission.servicePrice),
          formatCurrency(commission.amount),
          status
        ]);
      });

      // @ts-ignore
      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 62,
        styles: { fontSize: 9 },
        headerStyles: { fillColor: [99, 102, 241] }
      });

      doc.save(`Commissions_${format(new Date(), 'yyyyMMdd_HHmm')}.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    }
  };

  const renderStatusChip = (status: string) => {
    const statusConfig: Record<string, { label: string; gradient: string }> = {
      paid: {
        label: 'Paid',
        gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
      },
      voided: {
        label: 'Voided',
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
      },
      pending: {
        label: 'Pending',
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          background: config.gradient,
          color: 'white',
          fontWeight: 600,
          fontSize: '0.7rem',
          height: 24,
        }}
      />
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 10 }}>
      {/* Modern Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '0 0 32px 32px',
          pt: 3,
          pb: 4,
          px: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v6h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3,
          },
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            <Box>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                Earnings
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Showing {commissions.length} transaction{commissions.length !== 1 ? 's' : ''}
                {hasMore ? ' (load more below)' : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={() => setShowDatePicker(!showDatePicker)}
                sx={{
                  bgcolor: alpha('#ffffff', 0.2),
                  color: 'white',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    bgcolor: alpha('#ffffff', 0.3),
                  },
                }}
              >
                <FilterListIcon />
              </IconButton>
              <IconButton
                onClick={handleExportPDF}
                sx={{
                  bgcolor: alpha('#ffffff', 0.2),
                  color: 'white',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    bgcolor: alpha('#ffffff', 0.3),
                  },
                }}
              >
                <FileDownloadIcon />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ mt: 3, position: 'relative', zIndex: 2 }}>
        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Card
              sx={{
                borderRadius: '20px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '12px',
                      bgcolor: alpha('#ffffff', 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1.5,
                    }}
                  >
                    <AttachMoneyIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                    Commissions
                  </Typography>
                </Box>
                {summaryLoading ? (
                  <Skeleton variant="text" width={100} height={32} sx={{ bgcolor: alpha('#ffffff', 0.2) }} />
                ) : (
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                    {formatCurrency(commissionTotal)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6}>
            <Card
              sx={{
                borderRadius: '20px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '12px',
                      bgcolor: alpha('#ffffff', 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1.5,
                    }}
                  >
                    <TrendingUpIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                    Sale Splits
                  </Typography>
                </Box>
                {summaryLoading ? (
                  <Skeleton variant="text" width={100} height={32} sx={{ bgcolor: alpha('#ffffff', 0.2) }} />
                ) : (
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                    {formatCurrency(saleSplitsTotal)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Date Picker */}
        {showDatePicker && (
          <Card sx={{ mb: 3, borderRadius: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
            <CardContent>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </CardContent>
          </Card>
        )}

        {/* Commissions List */}
        <Typography variant="h6" sx={{ mb: 2, px: 1, fontWeight: 700 }}>
          Transactions
        </Typography>

        {errorMessage && !loading && (
          <Card sx={{ mb: 2, borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
            <CardContent>
              <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                {errorMessage}
              </Typography>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <Box>
            {[1, 2, 3].map((i) => (
              <Card key={i} sx={{ mb: 2, borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
                <CardContent>
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width="40%" height={20} />
                  <Skeleton variant="rectangular" height={40} sx={{ mt: 1, borderRadius: '8px' }} />
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : commissions.length > 0 ? (
          <Box>
            {commissions.map((commission) => (
              <Card
                key={commission.id}
                sx={{
                  mb: 2,
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
                        {commission.serviceName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Sale #{commission.saleNumber} • {format(convertTimestamp(commission.date), 'MMM d, yyyy')}
                      </Typography>
                    </Box>
                    {renderStatusChip(commission.status)}
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      p: 1.5,
                      borderRadius: '12px',
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Service Price
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(commission.servicePrice)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Your Earnings
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="primary">
                        {formatCurrency(commission.amount)}
                      </Typography>
                    </Box>
                  </Box>

                  {commission.customerName && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                      Customer: {commission.customerName}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 3 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  sx={{ minWidth: 180 }}
                >
                  {loadingMore ? 'Loading…' : 'Load More'}
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          <Card sx={{ borderRadius: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    mb: 3,
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                  }}
                >
                  <AttachMoneyIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  No commissions yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your earnings will appear here
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Container>

      {/* Bottom Navigation */}
      <BottomNav />
    </Box>
  );
};

export default CommissionsView;
